import type { Capture, Paragraph, Span, StoryBlock } from './types';

export const MAX_CAPTURES = 8;

/** Editor state: text blocks hold the contenteditable HTML, converted to safe spans on submit. */
export type DraftBlock =
  | { id: string; kind: 'text'; html: string }
  | { id: string; kind: 'capture'; capture: Capture };

/** Where a capture goes: the text block the caret was in, split into the HTML before and after it. */
export interface InsertPoint {
  blockId: string | null;
  sourceHtml: string;
  before: string;
  after: string;
}

let counter = 0;
export const blockId = () => `b${Date.now().toString(36)}${(counter++).toString(36)}`;

export const textBlock = (html = ''): DraftBlock => ({ id: blockId(), kind: 'text', html });

export function captureCount(blocks: DraftBlock[]): number {
  return blocks.filter((b) => b.kind === 'capture').length;
}

/** Only web and mail links survive; a bare domain gets https:// in front. */
export function safeHref(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`;
  if (!/^(https?:\/\/|mailto:)/i.test(withScheme)) return null;
  try {
    return new URL(withScheme).href;
  } catch {
    return null;
  }
}

const BLOCK_TAGS = new Set([
  'DIV',
  'P',
  'LI',
  'UL',
  'OL',
  'BLOCKQUOTE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'PRE',
]);
const sameMarks = (a: Span, b: Span) =>
  a.bold === b.bold && a.italic === b.italic && a.href === b.href;

/**
 * Converts contenteditable HTML into paragraphs of spans. Anything outside bold, italic and links
 * is dropped, so the server never receives markup from the page.
 */
export function htmlToParagraphs(html: string): Paragraph[] {
  const template = document.createElement('template');
  template.innerHTML = html;
  const paragraphs: Paragraph[] = [[]];
  const current = () => paragraphs[paragraphs.length - 1]!;
  const newParagraph = () => paragraphs.push([]);

  const walk = (node: Node, marks: Omit<Span, 'text'>) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent ?? '').replace(/ /g, ' ');
      if (text) current().push({ text, ...marks });
      return;
    }
    if (!(node instanceof Element)) return;
    const tag = node.tagName;
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEMPLATE') return;
    if (tag === 'BR') {
      newParagraph();
      return;
    }
    const next = { ...marks };
    if (tag === 'B' || tag === 'STRONG') next.bold = true;
    if (tag === 'I' || tag === 'EM') next.italic = true;
    if (node instanceof HTMLElement && tag === 'SPAN') {
      const weight = node.style.fontWeight;
      if (weight === 'bold' || Number(weight) >= 600) next.bold = true;
      if (node.style.fontStyle === 'italic') next.italic = true;
    }
    if (tag === 'A') {
      const href = safeHref(node.getAttribute('href') ?? '');
      if (href) next.href = href;
    }
    const isBlock = BLOCK_TAGS.has(tag);
    if (isBlock && current().length) newParagraph();
    node.childNodes.forEach((child) => walk(child, next));
    if (isBlock && current().length) newParagraph();
  };
  template.content.childNodes.forEach((child) => walk(child, {}));

  // Merge neighbouring spans with the same marks, keep at most one empty line in a row, trim the ends.
  const cleaned: Paragraph[] = [];
  for (const paragraph of paragraphs) {
    const merged: Paragraph = [];
    for (const span of paragraph) {
      const last = merged[merged.length - 1];
      if (last && sameMarks(last, span)) last.text += span.text;
      else merged.push({ ...span });
    }
    const empty = merged.every((s) => !s.text.trim());
    const previousEmpty = cleaned.length > 0 && cleaned[cleaned.length - 1]!.length === 0;
    if (empty) {
      if (!previousEmpty) cleaned.push([]);
    } else cleaned.push(merged);
  }
  while (cleaned.length && cleaned[0]!.length === 0) cleaned.shift();
  while (cleaned.length && cleaned[cleaned.length - 1]!.length === 0) cleaned.pop();
  return cleaned;
}

export function toStory(blocks: DraftBlock[]): StoryBlock[] {
  return blocks.flatMap((b): StoryBlock[] => {
    if (b.kind === 'capture') return [{ kind: 'capture', ...b.capture }];
    const paragraphs = htmlToParagraphs(b.html);
    return paragraphs.length ? [{ kind: 'text', paragraphs }] : [];
  });
}

/**
 * Puts a capture at the caret: the text block is split around it and writing continues in a new
 * block after it. If the block changed while the user was picking, the capture goes after it.
 */
export function insertCapture(
  blocks: DraftBlock[],
  at: InsertPoint,
  capture: Capture,
): { blocks: DraftBlock[]; focusId: string } {
  const captureBlock: DraftBlock = { id: blockId(), kind: 'capture', capture };
  const index = blocks.findIndex((b) => b.id === at.blockId);
  if (index < 0) {
    const tail = textBlock();
    return { blocks: [...blocks, captureBlock, tail], focusId: tail.id };
  }
  const target = blocks[index]!;
  if (target.kind === 'text' && target.html === at.sourceHtml) {
    const before = textBlock(at.before);
    const after = textBlock(at.after);
    const replacement = at.before.trim() ? [before, captureBlock, after] : [captureBlock, after];
    return {
      blocks: [...blocks.slice(0, index), ...replacement, ...blocks.slice(index + 1)],
      focusId: after.id,
    };
  }
  const following = blocks[index + 1];
  const tail = following?.kind === 'text' ? null : textBlock();
  const inserted = tail ? [captureBlock, tail] : [captureBlock];
  return {
    blocks: [...blocks.slice(0, index + 1), ...inserted, ...blocks.slice(index + 1)],
    focusId: tail?.id ?? following!.id,
  };
}

/** Removes a capture and joins the text around it back into one block. */
export function removeBlock(blocks: DraftBlock[], id: string): DraftBlock[] {
  const index = blocks.findIndex((b) => b.id === id);
  if (index < 0) return blocks;
  const previous = blocks[index - 1];
  const next = blocks[index + 1];
  if (previous?.kind === 'text' && next?.kind === 'text') {
    const joined = [previous.html, next.html]
      .filter((h) => h.trim())
      .map((h, i) => (i === 0 ? h : `<div>${h}</div>`));
    return [...blocks.slice(0, index - 1), textBlock(joined.join('')), ...blocks.slice(index + 2)];
  }
  const rest = [...blocks.slice(0, index), ...blocks.slice(index + 1)];
  return rest.length ? rest : [textBlock()];
}
